import React, { useEffect, useState } from 'react';
import { Workflow, Power } from 'lucide-react';
import { useToast } from '../../components/common';
import Inspector, { Band, Facts, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess } from '../../nucleus/access';
import api from '../../services/api';
import { GlyphFace, TriggerGlyph, RunDot } from './cells';
import { ReadSheet } from './sheets';
import { loadApproved } from '../nucleus/AppsCatalog';
import { autosApi, pluginsApi } from './data/api';
import { orderedChain, runItem } from './data/items';
import {
    TRIGGER_WORD as TRIGGER_WORDS, triggerKindWord, outcomeWord, dotTone,
} from './data/words';
import { whenWord as whenWords, eventWord, standingOf, isToolSchedule, plainError, schemaWord } from './data/lists';
import { ago, until, whenOf } from '../../nucleus/format';

// ONE AUTOMATION, or ONE FLOW, open on the Automations page: how it stands
// (On · Off · Can't run · Keeps failing), what starts it, what it runs (its
// plugin and tool, the realm it runs in, its settings by label), what ran, and
// every act on it. The acts are built once here (useAutomationActs) and feed
// both the inspector's bar and the right-click. A tool that runs automatically
// is never changed here: its acts open it on its tool.

export const TRIGGER_WORD = TRIGGER_WORDS;
/** When it runs, in words (data/lists.js whenWord). */
export const whenWord = (a, ctx) => whenWords(a, ctx);

const said = (v) => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : Array.isArray(v) ? v.join(', ') : v && typeof v === 'object' ? `${Object.keys(v).length} set` : String(v));
const RESERVED = ['privacy_realm_id', 'scope', 'remove_source', 'tool_id'];

/** Where a tool's "runs automatically" is changed: its plugin, its tool, the Runs automatically tab. */
export const scheduleQuery = (a) => `open=${encodeURIComponent(a.pluginId)}&tool=${encodeURIComponent(a.toolId || '')}&tab=schedule`;

export function useAutomationActs({ reload, onEdit, onEditFlow, onGone }) {
    const toast = useToast();
    const { groupId, spaceName, to, navigate } = usePlace();
    // a member whose realm lets them run plugins may run one and switch one
    // off; turning one on, editing, making and deleting stay an admin's
    const access = useSpaceAccess();
    const only = (what) => (access.isAdmin ? {} : { disabled: true, hint: `Only an admin of ${spaceName} can ${what}` });
    const runs = access.runPlugins ? {} : { disabled: true, hint: access.pluginHint };
    // the server's refusal, said plainly, for the toast
    const plainly = (fn, fallback) => async () => { try { await fn(); } catch (e) { throw plainError(e, fallback); } };
    const runNow = (a) => ({
        id: 'run', label: 'Run now', ...runs,
        run: plainly(async () => { await autosApi.trigger(groupId, a.id); toast.success(`Running ${a.name}.`); reload(); }, 'It could not run.'),
    });

    const forAutomation = (a) => {
        if (isToolSchedule(a) && a.pluginId) {
            return [
                { id: 'open', label: 'Open the tool', primary: true, run: () => navigate(to('plugins', null, scheduleQuery(a))) },
                runNow(a),
            ];
        }
        const on = standingOf(a).state !== 'off';
        return [
            { ...runNow(a), primary: true },
            on
                ? { id: 'off', label: 'Turn off', ...runs, run: plainly(async () => { await autosApi.setActive(groupId, a.id, false); toast.success(`${a.name} is off.`); reload(); }) }
                : { id: 'turn-on', label: 'Turn on', icon: <Power />, ...only('turn it on'), run: plainly(async () => { await autosApi.setActive(groupId, a.id, true); toast.success(`${a.name} is on.`); reload(); }) },
            { id: 'edit', label: 'Edit', ...only('edit it'), run: () => onEdit(a) },
            {
                id: 'delete', label: 'Delete', danger: true, ...only('delete it'),
                confirm: { title: `Delete ${a.name}?`, body: 'It stops and cannot be brought back. Its plugin stays set up.', label: 'Delete' },
                run: plainly(async () => { await autosApi.remove(groupId, a.id); toast.success(`${a.name} is deleted.`); onGone?.(a.id); reload(); }),
            },
        ];
    };

    const forFlow = (f) => [
        {
            id: 'run', label: 'Run', primary: true, ...runs,
            run: plainly(async () => { await autosApi.runFlow(groupId, f.id); toast.success(`Running ${f.name}.`); reload(); }, 'It could not run.'),
        },
        { id: 'edit', label: 'Edit', ...only('edit it'), run: () => onEditFlow(f) },
        {
            id: 'delete', label: 'Delete', danger: true, ...only('delete it'),
            confirm: { title: `Delete the flow ${f.name}?`, body: 'Only the flow goes. The automations in it stay.', label: 'Delete' },
            run: plainly(async () => { await autosApi.removeFlow(groupId, f.id); toast.success(`The flow ${f.name} is deleted.`); onGone?.(f.id); reload(); }),
        },
    ];

    return { forAutomation, forFlow };
}

/** A schema's name by its id, read once for an automation that starts on a change of one. */
function useSchemaName(groupId, schemaId) {
    const [name, setName] = useState(null);
    useEffect(() => {
        if (!schemaId) { setName(null); return undefined; }
        let live = true;
        Promise.resolve(api.getSchemas?.({ limit: 200, ...(groupId ? { group_id: groupId } : {}) }))
            .then((list) => { if (live) setName(schemaWord((list || []).find((x) => String(x.id) === String(schemaId)))); })
            .catch(() => {});
        return () => { live = false; };
    }, [groupId, schemaId]);
    return name;
}

/** The app that made it, by name: the automation's own word, else the apps connected here. */
function useMaker(groupId, item) {
    const [maker, setMaker] = useState(item.createdByApp || null);
    useEffect(() => {
        if (!item.createdByAppId || item.createdByApp) { setMaker(item.createdByApp || null); return undefined; }
        let live = true;
        setMaker(null);
        loadApproved(groupId)
            .then((rows) => { if (live) setMaker(rows.find((r) => String(r.app.id) === String(item.createdByAppId))?.app?.name || null); })
            .catch(() => {});
        return () => { live = false; };
    }, [groupId, item.createdByAppId, item.createdByApp]);
    return maker;
}

/**
 * `plugin`: its pluginItem here (null for an app's own), `tool`: its tool's
 * name, `flows`: the space's flows, `nameOf(id)`: another automation's name.
 */
export function AutomationInspector({ item, plugin = null, tool = null, flows = [], nameOf = () => null, actions, onClose, onOpen }) {
    const { groupId, spaceName, to, navigate } = usePlace();
    const [runs, setRuns] = useState(null);
    const [setup, setSetup] = useState(null);
    const [reading, setReading] = useState(false);
    const maker = useMaker(groupId, item);
    const schemaName = useSchemaName(groupId, item.trigger === 'event' ? item.schemaId : null);
    const isPlugin = !!plugin;
    const s = standingOf(item);
    const schedule = isToolSchedule(item);

    useEffect(() => {
        let live = true;
        setRuns(null);
        autosApi.runs(groupId, item.id, 5)
            .then((r) => { if (live) setRuns((r?.runs || []).filter((x) => x?.id).map((x) => runItem(x, item))); })
            .catch(() => { if (live) setRuns([]); });
        return () => { live = false; };
    }, [groupId, item.id, item.lastRun]); // eslint-disable-line react-hooks/exhaustive-deps

    // its plugin's setup here: the realm it runs in, by name, and its fields' labels
    useEffect(() => {
        if (!isPlugin) return undefined;
        let live = true;
        pluginsApi.setupConfig(item.pluginId, groupId, item.toolId).then((x) => { if (live) setSetup(x || {}); }).catch(() => { if (live) setSetup({}); });
        return () => { live = false; };
    }, [groupId, isPlugin, item.pluginId, item.toolId]);

    const inFlows = flows.filter((f) => f.steps.includes(item.id));
    const realmId = item.pluginConfig?.privacy_realm_id
        || (setup?.automations || []).find((a) => String(a.id) === String(item.id))?.privacy_realm_id || null;
    const realm = [...(setup?.eligible_realms || []), ...(setup?.run_realms || [])].find((r) => String(r.id) === String(realmId));
    const labelOf = (key) => {
        const f = (setup?.fields || []).find((x) => x.key === key);
        if (f?.label) return f.label;
        const words = String(key).replace(/[_-]+/g, ' ').trim();
        return words ? `${words[0].toUpperCase()}${words.slice(1)}` : key;
    };
    // ITS SETTINGS, AS FACTS: what it was given, by their labels
    const settings = Object.entries(item.pluginConfig || {})
        .filter(([k]) => !RESERVED.includes(k))
        .map(([k, v]) => ({ label: labelOf(k), value: said(v) }));
    const removes = item.pluginConfig?.remove_source === true;
    const fromName = item.triggerConfig?.from ? nameOf(item.triggerConfig.from) : null;
    const when = item.trigger === 'manual' ? null : whenWords(item, { schemaName, fromName });
    const openPlugin = () => navigate(to('plugins', null, `open=${encodeURIComponent(item.pluginId)}`));
    const openTool = (tab) => navigate(to('plugins', null, `open=${encodeURIComponent(item.pluginId)}&tool=${encodeURIComponent(item.toolId || '')}&tab=${tab}`));
    const madeBy = item.createdByAppId && (
        <>
            <button type="button" className="nx-link" onClick={() => navigate(to('apps', null, `open=${encodeURIComponent(item.createdByAppId)}`))}>{maker || 'Its app'}</button>
            {`, so it does only what ${maker || 'that app'} may do here`}
        </>
    );
    const tone = s.tone === 'live' ? 'live' : s.tone === 'warn' ? 'warn' : undefined;

    return (
        <Inspector
            face={<GlyphFace><TriggerGlyph trigger={item.trigger} /></GlyphFace>}
            tag={`${schedule ? 'Runs automatically' : 'Automation'} · ${s.word}`}
            title={item.name}
            sub={item.blurb || (schedule && tool ? `${tool}, run automatically` : triggerKindWord(item.trigger))}
            actions={actions}
            onClose={onClose}
        >
            <Band title={spaceName}>
                <Facts items={[
                    { label: 'State', value: s.word, tone },
                    { label: 'Starts', value: triggerKindWord(item.trigger) },
                    when && { label: schedule ? 'How often' : 'When', value: when },
                    { label: 'Last run', value: item.lastRun ? ago(item.lastRun) : 'Never', tone: item.failed ? 'live' : undefined },
                    item.next && ['on', 'running', 'failing'].includes(s.state) && { label: 'Next run', value: until(item.next) },
                    item.created && { label: 'Made', value: whenOf(item.created) },
                    madeBy && { label: 'Made by', value: madeBy },
                ]} />
                {s.why && <Note tone={tone}>{s.why}</Note>}
                {s.state === 'failing' && item.lastError && <Note tone="live">{item.lastError}</Note>}
            </Band>

            <Band title="What it runs">
                {isPlugin ? (
                    <Facts items={[
                        { label: 'Plugin', value: <button type="button" className="nx-link" onClick={openPlugin}>{plugin.name}</button> },
                        tool && (item.toolId || tool !== plugin.name) && { label: 'Tool', value: <button type="button" className="nx-link" onClick={() => openTool(schedule ? 'schedule' : 'run')}>{tool}</button> },
                        { label: 'Runs in', value: realm?.name || (setup ? (realmId ? 'A realm of this space' : 'No realm yet') : null), tone: setup && !realmId ? 'warn' : undefined },
                        removes && { label: 'After a run', value: 'Deletes what it read' },
                        ...settings,
                    ]} />
                ) : (
                    <Note>{item.plugin ? `${item.plugin}’s own work` : 'No plugin'}</Note>
                )}
                {inFlows.map((f) => (
                    <Row key={f.id} name={`In the flow ${f.name}`} sub={`Step ${f.steps.indexOf(item.id) + 1} of ${f.steps.length}`} onPress={() => onOpen(f.id)} />
                ))}
            </Band>

            <Band
                title="Recent runs"
                aside={item.pluginId ? <button type="button" className="nx-link" onClick={() => navigate(to('plugins', 'runs', `plugin=${encodeURIComponent(item.pluginId)}`))}>All its runs</button> : undefined}
            >
                {runs && runs.length === 0 && <Note>It has not run yet.</Note>}
                {(runs || []).map((r) => (
                    <Row
                        key={r.id}
                        name={<><RunDot tone={dotTone(r.outcome)} />{outcomeWord(r.outcome)}</>}
                        sub={r.refused || r.error || r.startedBy || undefined}
                        end={ago(r.started, { never: 'never' })}
                        endTone={dotTone(r.outcome) === 'failed' ? 'live' : undefined}
                        onPress={() => navigate(to('plugins', 'runs', `${item.pluginId ? `plugin=${encodeURIComponent(item.pluginId)}&` : ''}open=${encodeURIComponent(r.id)}`))}
                    />
                ))}
            </Band>

            <Band title="About" aside={<button type="button" className="nx-link" onClick={() => setReading(true)}>Details</button>}>
                {item.blurb && <Note>{item.blurb}</Note>}
            </Band>

            {reading && (
                <ReadSheet
                    title={`${item.name} · Details`}
                    parts={[
                        { label: 'What starts it', value: item.triggerConfig || {} },
                        { label: 'What its plugin is given', value: item.pluginConfig },
                        { label: 'Its app’s steps', value: item.appConfig },
                    ]}
                    onClose={() => setReading(false)}
                />
            )}
        </Inspector>
    );
}

/** A flow step's trigger, in words: the first starts as it is set to; the rest when the one before finishes. */
export function stepWord(m, members) {
    const tc = m.trigger_config || {};
    if (m.trigger_type === 'event' && tc.from && (tc.event === 'automation.completed' || tc.on === 'automation.completed' || !tc.event)) {
        const before = members.find((x) => x.automation_id === tc.from);
        return eventWord('automation.completed', { fromName: before?.name || null });
    }
    return triggerKindWord(m.trigger_type);
}

export function FlowInspector({ flow, nameOf, automationOf = () => null, actions, onClose, onOpen }) {
    const { groupId, spaceName } = usePlace();
    // the resolved flow: each member's app and trigger, in run order
    const [full, setFull] = useState(null);
    useEffect(() => {
        let live = true;
        setFull(null);
        autosApi.flow(groupId, flow.id).then((f) => { if (live) setFull(f); }).catch(() => { if (live) setFull({}); });
        return () => { live = false; };
    }, [groupId, flow.id, flow.steps.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

    const chain = full?.members ? orderedChain(full.members, full.edges) : null;
    // each member with its own trigger (the list's, where the flow's answer leaves it out)
    const members = (chain || flow.steps.map((id) => ({ automation_id: id, name: nameOf(id) }))).map((m) => {
        const a = automationOf(m.automation_id);
        return {
            ...m,
            name: m.name || a?.name || nameOf(m.automation_id),
            trigger_type: m.trigger_type || a?.trigger || null,
            trigger_config: m.trigger_config || a?.triggerConfig || {},
            app: m.app || a?.plugin || null,
        };
    });
    return (
        <Inspector
            face={<GlyphFace><Workflow strokeWidth={1.3} width={30} height={30} /></GlyphFace>}
            tag="Flow"
            title={flow.name}
            sub={flow.detail || undefined}
            actions={actions}
            onClose={onClose}
        >
            <Band title={spaceName}>
                <Facts items={[{ label: 'Steps', value: String(flow.steps.length) }]} />
            </Band>
            <Band title="Steps">
                {members.length === 0 && <Note>Nothing in it yet.</Note>}
                {members.map((m, i) => (
                    <Row
                        key={m.automation_id}
                        name={<><span className="plugins-stepnum">{String(i + 1).padStart(2, '0')}</span>{m.name}</>}
                        sub={[m.app, m.trigger_type ? stepWord(m, members) : null].filter(Boolean).join(' · ') || undefined}
                        onPress={() => onOpen(m.automation_id)}
                    />
                ))}
            </Band>
        </Inspector>
    );
}
